import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Chip,
  IconButton,
  Stack,
  Divider,
  Alert,
  InputAdornment,
  Pagination,
  Collapse,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import PlaylistAddCheckCircleOutlinedIcon from "@mui/icons-material/PlaylistAddCheckCircleOutlined";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { addDays, endOfMonth, format, isBefore, isSameDay, startOfDay, startOfMonth } from "date-fns";
import { de } from "date-fns/locale";
import {
  addPackageTimeBlockApi,
  getPackageTimeBlocksApi,
  removeAllPackageTimeBlocksByDateApi,
  removePackageTimeBlockApi,
} from "../../Apis/packageTimeBlocks/packageTimeBlocks";
import { GetAllPackagesApi } from "../../Apis/Package/Package";
import { getAllSettingsApi } from "../../Apis/settings/settings";
import { getOpeningOverridesApi } from "../../Apis/openingOverrides/openingOverrides";
import { useToast } from "../../Hooks/useToast";

const WORKFLOW_STEPS = [
  { id: 1, label: "Datum", icon: <EventNoteOutlinedIcon fontSize="small" /> },
  { id: 2, label: "Pakete", icon: <Inventory2OutlinedIcon fontSize="small" /> },
  { id: 3, label: "Zeitfenster", icon: <ScheduleOutlinedIcon fontSize="small" /> },
  { id: 4, label: "Hinweis", icon: <NotesOutlinedIcon fontSize="small" /> },
  { id: 5, label: "Speichern", icon: <PlaylistAddCheckCircleOutlinedIcon fontSize="small" /> },
];
const TIME_STEP_MINUTES = 30;
const DEFAULT_OPEN_TIME = "10:00";
const DEFAULT_CLOSE_TIME = "22:00";
const toMinutes = (label) => {
  const [hh, mm] = String(label).split(":").map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
};
const hasTimeOverlap = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && aEnd > bStart;

const PackageTimeBlocks = () => {
  const { toastError, toastSuccess } = useToast();
  const [packages, setPackages] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [date, setDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [reason, setReason] = useState("");
  const [packageQuery, setPackageQuery] = useState("");
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [operatingHours, setOperatingHours] = useState({
    season: "summer",
    open: DEFAULT_OPEN_TIME,
    close: DEFAULT_CLOSE_TIME,
  });
  const [openingOverrides, setOpeningOverrides] = useState([]);
  const [daysOff, setDaysOff] = useState([]);
  const isMountedRef = useRef(false);
  const isLoadInFlightRef = useRef(false);
  const hasRequiredFields = Boolean(
    date && startTime && endTime && selectedPackages.length > 0
  );

  const formatDateForUi = useCallback((value) => {
    if (!value) return "";
    const [y, m, d] = String(value).split("-");
    if (!y || !m || !d) return value;
    return `${d}.${m}.${y}`;
  }, []);

  const withTimeout = useCallback((promise, ms, label) => {
    let timerId = null;

    const timeoutPromise = new Promise((_, reject) => {
      timerId = setTimeout(
        () => reject(new Error(`${label} timeout after ${ms}ms`)),
        ms
      );
    });

    return Promise.race([promise, timeoutPromise]).finally(() => {
      if (timerId) clearTimeout(timerId);
    });
  }, []);

  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (isLoadInFlightRef.current) return;

    isLoadInFlightRef.current = true;

    try {
      if (!silent && isMountedRef.current) {
        setIsLoading(true);
      }

      const [packagesResult, blocksResult, settingsResult, overridesResult] = await Promise.allSettled([
        withTimeout(GetAllPackagesApi({ page: 1, limit: 500 }), 10000, "packages"),
        withTimeout(getPackageTimeBlocksApi(), 10000, "package-time-blocks"),
        withTimeout(getAllSettingsApi(), 10000, "settings"),
        withTimeout(getOpeningOverridesApi(), 10000, "opening-overrides"),
      ]);

      const packagesRes =
        packagesResult.status === "fulfilled" ? packagesResult.value : null;
      const blocksRes =
        blocksResult.status === "fulfilled" ? blocksResult.value : null;
      const settingsRes =
        settingsResult.status === "fulfilled" ? settingsResult.value : null;
      const overridesRes =
        overridesResult.status === "fulfilled" ? overridesResult.value : null;

      if (isMountedRef.current) {
        setPackages(Array.isArray(packagesRes?.data) ? packagesRes.data : []);
        setBlocks(Array.isArray(blocksRes?.data) ? blocksRes.data : []);
        setOpeningOverrides(
          Array.isArray(overridesRes?.openingOverrides) ? overridesRes.openingOverrides : []
        );

        const settingsData = settingsRes?.data || {};
        setDaysOff(Array.isArray(settingsData?.daysOff) ? settingsData.daysOff : []);
        const selectedSeason =
          settingsData?.season === "winter" || settingsData?.season === "summer"
            ? settingsData.season
            : "summer";
        const seasonTimings =
          settingsData?.timings?.[selectedSeason] ||
          settingsData?.timings?.summer ||
          settingsData?.timings?.winter ||
          {};

        const openValue =
          typeof seasonTimings?.open === "string" && /^\d{2}:\d{2}$/.test(seasonTimings.open)
            ? seasonTimings.open
            : DEFAULT_OPEN_TIME;
        const closeValue =
          typeof seasonTimings?.close === "string" && /^\d{2}:\d{2}$/.test(seasonTimings.close)
            ? seasonTimings.close
            : DEFAULT_CLOSE_TIME;

        setOperatingHours({
          season: selectedSeason,
          open: openValue,
          close: closeValue,
        });
      }

      if (packagesResult.status === "rejected" || packagesRes?.status === "error") {
        const details =
          packagesResult.status === "rejected"
            ? packagesResult.reason?.message
            : packagesRes?.error;
        toastError(
          `Paketliste konnte nicht geladen werden${details ? `: ${details}` : ""}`
        );
      }
      if (blocksResult.status === "rejected") {
        toastError(
          `Paket-Zeitblockierungen konnten nicht geladen werden${
            blocksResult.reason?.message ? `: ${blocksResult.reason.message}` : ""
          }`
        );
      }
    } finally {
      isLoadInFlightRef.current = false;
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [toastError, withTimeout]);

  useEffect(() => {
    isMountedRef.current = true;
    loadData();

    return () => {
      isMountedRef.current = false;
    };
  }, [loadData]);

  const sortedBlocks = useMemo(() => {
    const copy = [...blocks];
    copy.sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      if (a.startTime !== b.startTime) return a.startTime.localeCompare(b.startTime);
      return a.endTime.localeCompare(b.endTime);
    });
    return copy;
  }, [blocks]);

  const groupedByRange = useMemo(() => {
    const groups = {};
    for (const block of sortedBlocks) {
      const key = `${block.date}|${block.startTime}|${block.endTime}`;
      if (!groups[key]) {
        groups[key] = {
          date: block.date,
          startTime: block.startTime,
          endTime: block.endTime,
          items: [],
        };
      }
      groups[key].items.push(block);
    }
    return Object.values(groups);
  }, [sortedBlocks]);

  // Split blockings into upcoming (today or later) and past (before today).
  // Past days must not appear in the main list – they move to a collapsible section.
  const GROUPS_PER_PAGE = 6;
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [showPast, setShowPast] = useState(false);

  const todayKey = useMemo(
    () => format(startOfDay(new Date()), "yyyy-MM-dd"),
    []
  );

  const { upcomingGroups, pastGroups } = useMemo(() => {
    const upcoming = [];
    const past = [];
    for (const group of groupedByRange) {
      const groupDateKey = String(group.date).slice(0, 10);
      if (groupDateKey < todayKey) {
        past.push(group);
      } else {
        upcoming.push(group);
      }
    }
    // Upcoming: nearest first (ascending, already sorted).
    // Past: most recent first.
    past.reverse();
    return { upcomingGroups: upcoming, pastGroups: past };
  }, [groupedByRange, todayKey]);

  const upcomingTotalPages = Math.max(
    1,
    Math.ceil(upcomingGroups.length / GROUPS_PER_PAGE)
  );
  const pastTotalPages = Math.max(
    1,
    Math.ceil(pastGroups.length / GROUPS_PER_PAGE)
  );

  useEffect(() => {
    if (upcomingPage > upcomingTotalPages) setUpcomingPage(upcomingTotalPages);
  }, [upcomingPage, upcomingTotalPages]);
  useEffect(() => {
    if (pastPage > pastTotalPages) setPastPage(pastTotalPages);
  }, [pastPage, pastTotalPages]);

  const pagedUpcomingGroups = useMemo(
    () =>
      upcomingGroups.slice(
        (upcomingPage - 1) * GROUPS_PER_PAGE,
        upcomingPage * GROUPS_PER_PAGE
      ),
    [upcomingGroups, upcomingPage]
  );
  const pagedPastGroups = useMemo(
    () =>
      pastGroups.slice(
        (pastPage - 1) * GROUPS_PER_PAGE,
        pastPage * GROUPS_PER_PAGE
      ),
    [pastGroups, pastPage]
  );

  const selectedPackageIdSet = useMemo(
    () => new Set(selectedPackages.map((pkg) => pkg?._id).filter(Boolean)),
    [selectedPackages]
  );
  const selectedDateKey = useMemo(
    () => (date ? format(date, "yyyy-MM-dd") : ""),
    [date]
  );

  const selectedDatePackageBlocks = useMemo(() => {
    if (!selectedDateKey || selectedPackageIdSet.size === 0) return [];

    return blocks
      .filter((block) => {
        const blockDate = String(block?.date || "").slice(0, 10);
        if (blockDate !== selectedDateKey) return false;
        const blockPackageId = String(block?.packageId?._id || block?.packageId || "");
        return selectedPackageIdSet.has(blockPackageId);
      })
      .map((block) => {
        const blockStartMinutes = toMinutes(block?.startTime);
        const blockEndMinutes = toMinutes(block?.endTime);
        return {
          ...block,
          blockStartMinutes,
          blockEndMinutes,
        };
      })
      .filter(
        (block) =>
          typeof block.blockStartMinutes === "number" &&
          typeof block.blockEndMinutes === "number" &&
          block.blockStartMinutes < block.blockEndMinutes
      );
  }, [blocks, selectedDateKey, selectedPackageIdSet]);

  const filteredPackages = useMemo(() => {
    const query = packageQuery.trim().toLowerCase();
    if (!query) return packages;
    return packages.filter((pkg) => (pkg?.name || "").toLowerCase().includes(query));
  }, [packages, packageQuery]);

  const operatingRange = useMemo(() => {
    const openMinutes = toMinutes(operatingHours?.open);
    const closeMinutes = toMinutes(operatingHours?.close);

    const validRange =
      typeof openMinutes === "number" &&
      typeof closeMinutes === "number" &&
      closeMinutes > openMinutes;

    if (!validRange) {
      return {
        season: "summer",
        openLabel: DEFAULT_OPEN_TIME,
        closeLabel: DEFAULT_CLOSE_TIME,
        openMinutes: 10 * 60,
        closeMinutes: 22 * 60,
      };
    }

    return {
      season: operatingHours?.season || "summer",
      openLabel: operatingHours?.open || DEFAULT_OPEN_TIME,
      closeLabel: operatingHours?.close || DEFAULT_CLOSE_TIME,
      openMinutes,
      closeMinutes,
    };
  }, [operatingHours]);

  // Effective schedule for the currently selected date:
  // free day / full-day closed => closed; date override => its periods;
  // otherwise the weekly (season) default range.
  const effectiveDay = useMemo(() => {
    const label = (min) =>
      `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;

    if (!selectedDateKey) {
      return {
        isClosed: false,
        closedReason: "",
        intervals: [{ startMin: operatingRange.openMinutes, endMin: operatingRange.closeMinutes }],
      };
    }

    if (daysOff.includes(selectedDateKey)) {
      return { isClosed: true, closedReason: "freeDay", intervals: [] };
    }

    const override = openingOverrides.find((o) => o.date === selectedDateKey);
    if (override) {
      if (override.closedAllDay || !(override.periods || []).length) {
        return { isClosed: true, closedReason: "closed", intervals: [] };
      }
      const intervals = override.periods
        .map((p) => ({ startMin: toMinutes(p.start), endMin: toMinutes(p.end) }))
        .filter((iv) => typeof iv.startMin === "number" && typeof iv.endMin === "number" && iv.startMin < iv.endMin)
        .sort((a, b) => a.startMin - b.startMin);
      return { isClosed: intervals.length === 0, closedReason: "closed", intervals };
    }

    return {
      isClosed: false,
      closedReason: "",
      intervals: [{ startMin: operatingRange.openMinutes, endMin: operatingRange.closeMinutes }],
      _label: label,
    };
  }, [selectedDateKey, daysOff, openingOverrides, operatingRange]);

  // Human-readable effective opening text, e.g. "12:00-21:00" or "10:00-13:00, 15:00-21:00".
  const effectiveRangeLabel = useMemo(() => {
    if (effectiveDay.isClosed) return "";
    return effectiveDay.intervals
      .map(
        (iv) =>
          `${String(Math.floor(iv.startMin / 60)).padStart(2, "0")}:${String(iv.startMin % 60).padStart(2, "0")} - ` +
          `${String(Math.floor(iv.endMin / 60)).padStart(2, "0")}:${String(iv.endMin % 60).padStart(2, "0")}`
      )
      .join(", ");
  }, [effectiveDay]);

  // Time-slot labels generated ONLY within the effective intervals.
  const timeSlots = useMemo(() => {
    const set = new Set();
    effectiveDay.intervals.forEach((iv) => {
      for (let t = iv.startMin; t <= iv.endMin; t += TIME_STEP_MINUTES) {
        set.add(t);
      }
      set.add(iv.endMin);
    });
    return Array.from(set)
      .sort((a, b) => a - b)
      .map((min) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`);
  }, [effectiveDay]);

  // Which interval (if any) contains a given minute as a START-eligible point.
  const intervalForStart = (min) =>
    effectiveDay.intervals.find((iv) => min >= iv.startMin && min < iv.endMin) || null;
  // Which interval allows an END at `min` given a chosen start.
  const sameIntervalEnd = (startMin, endMin) =>
    effectiveDay.intervals.some((iv) => startMin >= iv.startMin && endMin <= iv.endMin && startMin < endMin);

  const startLabel = useMemo(
    () => (startTime ? format(startTime, "HH:mm") : ""),
    [startTime]
  );
  const endLabel = useMemo(
    () => (endTime ? format(endTime, "HH:mm") : ""),
    [endTime]
  );

  const doesRangeOverlapSelectedBlocks = useCallback(
    (rangeStartMinutes, rangeEndMinutes) => {
      if (selectedDatePackageBlocks.length === 0) return false;
      if (typeof rangeStartMinutes !== "number" || typeof rangeEndMinutes !== "number") {
        return false;
      }
      if (rangeStartMinutes >= rangeEndMinutes) return false;

      return selectedDatePackageBlocks.some((block) =>
        hasTimeOverlap(
          rangeStartMinutes,
          rangeEndMinutes,
          block.blockStartMinutes,
          block.blockEndMinutes
        )
      );
    },
    [selectedDatePackageBlocks]
  );

  const hasSelectedRangeConflict = useMemo(() => {
    if (!hasRequiredFields) return false;

    const startMinutes = toMinutes(startLabel);
    const endMinutes = toMinutes(endLabel);
    return doesRangeOverlapSelectedBlocks(startMinutes, endMinutes);
  }, [hasRequiredFields, startLabel, endLabel, doesRangeOverlapSelectedBlocks]);

  const isFormReady = hasRequiredFields && !hasSelectedRangeConflict && !effectiveDay.isClosed;

  const monthDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
      days.push(new Date(d));
    }
    return days;
  }, [currentMonth]);

  const prevMonth = useCallback(
    () => setCurrentMonth(addDays(startOfMonth(currentMonth), -1)),
    [currentMonth]
  );
  const nextMonth = useCallback(
    () => setCurrentMonth(addDays(endOfMonth(currentMonth), 1)),
    [currentMonth]
  );
  const resetMonthToToday = useCallback(() => setCurrentMonth(new Date()), []);

  const togglePackage = useCallback((pkg) => {
    setSelectedPackages((prev) => {
      const exists = prev.some((item) => item?._id === pkg?._id);
      if (exists) return prev.filter((item) => item?._id !== pkg?._id);
      return [...prev, pkg];
    });
  }, []);

  const makeTimeValue = useCallback(
    (label) => {
      const [hh, mm] = String(label).split(":").map(Number);
      const base = date ? new Date(date) : new Date();
      base.setHours(Number.isNaN(hh) ? 0 : hh, Number.isNaN(mm) ? 0 : mm, 0, 0);
      return base;
    },
    [date]
  );

  const handleAdd = async () => {
    if (!date || !startTime || !endTime || selectedPackages.length === 0) {
      toastError("Bitte Datum, Zeitbereich und mindestens ein Paket wahlen");
      return;
    }

    const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
    const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();

    if (startMinutes >= endMinutes) {
      toastError("Startzeit muss vor Endzeit liegen");
      return;
    }
    if (doesRangeOverlapSelectedBlocks(startMinutes, endMinutes)) {
      toastError(
        "Dieses Zeitfenster ist fur mindestens ein ausgewahltes Paket bereits blockiert"
      );
      return;
    }

    const dateValue = format(date, "yyyy-MM-dd");
    const startValue = format(startTime, "HH:mm");
    const endValue = format(endTime, "HH:mm");

    try {
      setIsSaving(true);
      let addedCount = 0;
      const failed = [];

      for (const pkg of selectedPackages) {
        try {
          await addPackageTimeBlockApi({
            packageId: pkg._id,
            date: dateValue,
            startTime: startValue,
            endTime: endValue,
            reason,
          });
          addedCount += 1;
        } catch (error) {
          const message =
            error?.response?.data?.error || error?.message || "Unbekannter Fehler";
          failed.push(`${pkg.name}: ${message}`);
        }
      }

      await loadData({ silent: true });

      if (addedCount > 0) {
        toastSuccess(`${addedCount} Paket-Zeitblockierung(en) hinzugefugt`);
      }
      if (failed.length > 0) {
        toastError(failed.slice(0, 2).join(" | "));
      }

      setSelectedPackages([]);
      setReason("");
      setStartTime(null);
      setEndTime(null);
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = useCallback(() => {
    setDate(null);
    setStartTime(null);
    setEndTime(null);
    setReason("");
    setPackageQuery("");
    setSelectedPackages([]);
  }, []);

  const handleDeleteOne = async (id) => {
    try {
      setIsSaving(true);
      await removePackageTimeBlockApi(id);
      await loadData({ silent: true });
      toastSuccess("Paket-Zeitblockierung entfernt");
    } catch (error) {
      toastError(error?.response?.data?.error || "Loschen fehlgeschlagen");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDate = async (targetDate) => {
    try {
      setIsSaving(true);
      await removeAllPackageTimeBlocksByDateApi(targetDate);
      await loadData({ silent: true });
      toastSuccess("Alle Paket-Blockierungen fur das Datum entfernt");
    } catch (error) {
      toastError(error?.response?.data?.error || "Loschen fehlgeschlagen");
    } finally {
      setIsSaving(false);
    }
  };

  const renderGroupCard = (group, { muted = false } = {}) => (
    <Box
      key={`${group.date}|${group.startTime}|${group.endTime}`}
      sx={{
        border: "1px solid #e5e7eb",
        borderRadius: 2,
        p: 2.25,
        background: muted ? "#f8fafc" : "#fff",
        opacity: muted ? 0.85 : 1,
        transition: "all 0.2s ease",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          borderColor: "#cbd5e1",
          boxShadow: "0 6px 16px rgba(15,23,42,0.06)",
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1}
        mb={1.25}
      >
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
            {formatDateForUi(group.date)} | {group.startTime} - {group.endTime}
          </Typography>
          <Typography variant="caption" color="#64748b">
            {group.items.length} Paket(e) in diesem Zeitfenster blockiert
          </Typography>
        </Stack>
        <Button
          size="small"
          color="error"
          onClick={() => handleDeleteDate(group.date)}
          disabled={isSaving}
          startIcon={<DeleteSweepOutlinedIcon fontSize="small" />}
          sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0 }}
        >
          Ganzen Tag loschen
        </Button>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={1}>
        {group.items.map((item) => (
          <Chip
            key={item._id}
            label={item?.packageId?.name || "Paket"}
            onDelete={() => handleDeleteOne(item._id)}
            deleteIcon={<DeleteIcon />}
            disabled={isSaving}
            sx={{
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              fontWeight: 600,
            }}
          />
        ))}
      </Box>

      {Array.from(new Set(group.items.map((i) => i.reason).filter(Boolean))).length > 0 && (
        <Typography variant="caption" color="#64748b" sx={{ display: "block", mt: 1 }}>
          Hinweis: {Array.from(new Set(group.items.map((i) => i.reason).filter(Boolean))).join(" | ")}
        </Typography>
      )}
    </Box>
  );

  const groupsGridSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "repeat(1, minmax(0, 1fr))",
      md: "repeat(2, minmax(0, 1fr))",
      xl: "repeat(3, minmax(0, 1fr))",
    },
    gap: 1.5,
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          border: "1px solid #e5e7eb",
          borderRadius: 3,
          p: { xs: 2, md: 3 },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#dbeafe",
              color: "#1d4ed8",
            }}
          >
            <AutoAwesomeRoundedIcon fontSize="small" />
          </Box>
          <Typography variant="h6" fontWeight={700} color="#0f172a">
            Paket-Zeitblockierungen
          </Typography>
        </Stack>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 2,
            px: 2,
            py: 2,
          }}
        >
          <CircularProgress size={20} />
          <Typography color="#334155">Lade Paket-Zeitblockierungen...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: "1px solid #e5e7eb",
        borderRadius: 3,
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#dbeafe",
            color: "#1d4ed8",
            boxShadow: "inset 0 0 0 1px #bfdbfe",
          }}
        >
          <AutoAwesomeRoundedIcon fontSize="small" />
        </Box>
        <Typography variant="h5" color="#0f172a" fontWeight={800}>
          Pakete fur bestimmte Zeiten blockieren
        </Typography>
      </Stack>
      <Typography variant="body2" color="#475569" mb={2.5}>
        Einfache Schrittfolge: Datum und Pakete wahlen, Zeitfenster setzen, dann speichern.
      </Typography>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        mb={2.5}
        sx={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          p: 1.25,
        }}
      >
        {WORKFLOW_STEPS.map((step) => (
          <Box
            key={step.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 0.5,
              borderRadius: 1.25,
              background: "#ffffff",
              border: "1px solid #e2e8f0",
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "999px",
                fontSize: 12,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#e2e8f0",
                color: "#334155",
              }}
            >
              {step.id}
            </Box>
            <Box sx={{ color: "#475569", display: "flex", alignItems: "center" }}>
              {step.icon}
            </Box>
            <Typography variant="caption" fontWeight={700} color="#334155">
              {step.label}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Divider sx={{ mb: 2.5 }} />

      <Box
        sx={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          p: { xs: 1.5, md: 2 },
          mb: 2.5,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <IconButton onClick={prevMonth} size="small">
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={800} color="#1e293b">
            {format(currentMonth, "MMMM yyyy", { locale: de })}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <IconButton onClick={resetMonthToToday} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={nextMonth} size="small">
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2.5 }}>
          {monthDays.map((dayItem) => {
            const isPastDay = isBefore(startOfDay(dayItem), startOfDay(new Date()));
            const isSelected = !isPastDay && date && isSameDay(date, dayItem);
            const isToday = isSameDay(dayItem, new Date());

            // Day state colors (editable palette below):
            const dateKey = format(dayItem, "yyyy-MM-dd");
            const isFreeDay = daysOff.includes(dateKey);
            const override = openingOverrides.find((o) => o.date === dateKey);
            const isClosedOverride =
              Boolean(override) && (override.closedAllDay || !(override.periods || []).length);
            const isAdjusted = Boolean(override) && !isClosedOverride;
            const isClosed = isFreeDay || isClosedOverride;

            // Resolve border/background per state (priority: selected > past > closed > adjusted > today).
            let borderColor = "#d1d5db";
            let bgColor = "#ffffff";
            if (!isPastDay && !isSelected) {
              if (isClosed) { borderColor = "#dc2626"; bgColor = "#fee2e2"; }
              else if (isAdjusted) { borderColor = "#f59e0b"; bgColor = "#fff7ed"; }
              else if (isToday) { borderColor = "#ef4444"; }
            }

            return (
              <Box
                key={dayItem.toISOString()}
                onClick={() => {
                  if (!isPastDay) setDate(dayItem);
                }}
                title={
                  isClosed
                    ? (isFreeDay ? "Freier Tag - geschlossen" : "Ganztägig geschlossen")
                    : isAdjusted
                    ? "Angepasste Öffnungszeiten"
                    : "Standardöffnungszeiten"
                }
                sx={{
                  position: "relative",
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1.1,
                  cursor: isPastDay ? "not-allowed" : "pointer",
                  fontWeight: isSelected ? 700 : 500,
                  fontSize: 14,
                  border: isPastDay
                    ? "1px solid #d1d5db"
                    : isSelected
                    ? "2px solid #caa96a"
                    : `2px solid ${borderColor}`,
                  background: isPastDay ? "#e5e7eb" : isSelected ? "#caa96a" : bgColor,
                  color: isPastDay ? "#9ca3af" : isSelected ? "#111827" : "#1f2937",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: isPastDay ? "#e5e7eb" : isSelected ? "#caa96a" : "#f8fafc",
                  },
                }}
              >
                {!isPastDay && !isSelected && (isAdjusted || isClosed) && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 4,
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: isClosed ? "#dc2626" : "#f59e0b",
                    }}
                  />
                )}
                <Box
                  component="span"
                  sx={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", opacity: 0.65 }}
                >
                  {format(dayItem, "EEEEEE", { locale: de })}
                </Box>
                <Box component="span">{format(dayItem, "d")}</Box>
              </Box>
            );
          })}
        </Box>

        {/* Day-state legend */}
        <Stack direction="row" spacing={2.5} sx={{ mt: 1, mb: 2, flexWrap: "wrap" }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid #f59e0b", background: "#fff7ed" }} />
            <Typography variant="caption" color="#475569">Angepasste Öffnungszeiten</Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid #dc2626", background: "#fee2e2" }} />
            <Typography variant="caption" color="#475569">Geschlossen / Freier Tag</Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid #caa96a", background: "#caa96a" }} />
            <Typography variant="caption" color="#475569">Ausgewählt</Typography>
          </Stack>
        </Stack>

        {date && effectiveDay.isClosed && (
          <Alert
            severity="warning"
            sx={{ mb: 2, border: "1px solid #fde68a", background: "#fffbeb" }}
          >
            {effectiveDay.closedReason === "freeDay"
              ? `Dieser Tag (${format(date, "dd.MM.yyyy")}) ist als freier Tag markiert und ganztagig geschlossen. Es gibt nichts zu blockieren.`
              : `Dieser Tag (${format(date, "dd.MM.yyyy")}) ist ganztagig geschlossen. Es gibt nichts zu blockieren.`}
          </Alert>
        )}

        <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box
                sx={{
                  background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
                  border: "1px solid #dbe3ef",
                  borderRadius: 2,
                  p: 1.25,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  mb={1}
                >
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Inventory2OutlinedIcon sx={{ fontSize: 18, color: "#334155" }} />
                    <Typography variant="subtitle2" color="#1e293b" fontWeight={800}>
                      Pakete auswahlen
                    </Typography>
                    <Chip
                      size="small"
                      label={`${selectedPackages.length} gewahlt`}
                      sx={{
                        height: 22,
                        fontWeight: 700,
                        background: "#dbeafe",
                        color: "#1e40af",
                        border: "1px solid #bfdbfe",
                      }}
                    />
                  </Stack>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setSelectedPackages([])}
                    disabled={selectedPackages.length === 0 || isSaving}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#475569",
                      px: 1,
                      minWidth: "auto",
                    }}
                  >
                    Auswahl leeren
                  </Button>
                </Stack>

                <TextField
                  fullWidth
                  size="small"
                  value={packageQuery}
                  onChange={(event) => setPackageQuery(event.target.value)}
                  placeholder="Paket suchen..."
                  sx={{ background: "#ffffff", mb: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box
                  sx={{
                    maxHeight: 192,
                    overflowY: "auto",
                    pr: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(1, minmax(0, 1fr))",
                        sm: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(3, minmax(0, 1fr))",
                      },
                      gap: 1,
                    }}
                  >
                    {filteredPackages.map((pkg) => {
                      const isSelected = selectedPackageIdSet.has(pkg?._id);
                      return (
                        <ButtonBase
                          key={pkg?._id || pkg?.name}
                          onClick={() => togglePackage(pkg)}
                          disabled={isSaving || (Boolean(date) && effectiveDay.isClosed)}
                          sx={{
                            width: "100%",
                            textAlign: "left",
                            borderRadius: 1.5,
                            border: isSelected ? "1px solid #60a5fa" : "1px solid #d1d5db",
                            background: isSelected ? "#eff6ff" : "#ffffff",
                            px: 1.25,
                            py: 0.9,
                            transition: "all 0.18s ease",
                            "&:hover": {
                              borderColor: isSelected ? "#3b82f6" : "#94a3b8",
                              background: isSelected ? "#dbeafe" : "#f8fafc",
                            },
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" width="100%">
                            {isSelected ? (
                              <CheckCircleRoundedIcon sx={{ fontSize: 18, color: "#2563eb" }} />
                            ) : (
                              <RadioButtonUncheckedRoundedIcon
                                sx={{ fontSize: 18, color: "#94a3b8" }}
                              />
                            )}
                            <Typography
                              variant="body2"
                              fontWeight={isSelected ? 700 : 600}
                              color={isSelected ? "#1e3a8a" : "#334155"}
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {pkg?.name || "Paket"}
                            </Typography>
                          </Stack>
                        </ButtonBase>
                      );
                    })}
                  </Box>

                  {filteredPackages.length === 0 && (
                    <Box
                      sx={{
                        border: "1px dashed #cbd5e1",
                        borderRadius: 1.5,
                        px: 1.25,
                        py: 1.5,
                        background: "#ffffff",
                      }}
                    >
                      <Typography variant="body2" color="#64748b">
                        Keine Pakete zur Suchanfrage gefunden.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  border: "1px solid #dbe3ef",
                  borderRadius: 2,
                  background: "#f8fafc",
                  p: 1.25,
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2" fontWeight={800} color="#1e293b">
                    Von
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={startLabel || "--:--"}
                      sx={{
                        height: 22,
                        fontWeight: 700,
                        background: startLabel ? "#dbeafe" : "#e2e8f0",
                        color: startLabel ? "#1e40af" : "#64748b",
                      }}
                    />
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setStartTime(null)}
                      disabled={!startTime || isSaving}
                      sx={{
                        textTransform: "none",
                        minWidth: "auto",
                        px: 0.75,
                        color: "#64748b",
                        fontWeight: 700,
                      }}
                    >
                      Loschen
                    </Button>
                  </Stack>
                </Stack>
                <Typography variant="caption" color="#64748b" sx={{ display: "block", mb: 1 }}>
                  Verfügbare Zeit: {date ? (effectiveDay.isClosed ? "geschlossen" : effectiveRangeLabel) : `${operatingRange.openLabel} - ${operatingRange.closeLabel}`}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(4, minmax(0, 1fr))",
                      sm: "repeat(5, minmax(0, 1fr))",
                      md: "repeat(6, minmax(0, 1fr))",
                    },
                    gap: 1,
                  }}
                >
                  {timeSlots.map((slot) => {
                    const slotMinutes = toMinutes(slot);
                    const endMinutes = endLabel ? toMinutes(endLabel) : null;
                    // A start must fall inside an effective opening interval.
                    const disabledByRange =
                      typeof slotMinutes !== "number" || !intervalForStart(slotMinutes);
                    const disabledByBlockedStartSlot =
                      selectedDatePackageBlocks.length > 0 &&
                      typeof slotMinutes === "number" &&
                      selectedDatePackageBlocks.some(
                        (block) =>
                          slotMinutes >= block.blockStartMinutes &&
                          slotMinutes < block.blockEndMinutes
                      );
                    const disabledByCurrentEndOverlap =
                      typeof endMinutes === "number" &&
                      doesRangeOverlapSelectedBlocks(slotMinutes, endMinutes);
                    const disabled =
                      disabledByRange ||
                      (typeof endMinutes === "number" && slotMinutes >= endMinutes) ||
                      disabledByBlockedStartSlot ||
                      disabledByCurrentEndOverlap;
                    const selected = startLabel === slot;

                    return (
                      <ButtonBase
                        key={`start-${slot}`}
                        onClick={() => setStartTime(makeTimeValue(slot))}
                        disabled={disabled || isSaving}
                        sx={{
                          width: "100%",
                          minWidth: 0,
                          height: 40,
                          borderRadius: 999,
                          px: 0.75,
                          border: selected ? "2px solid #caa96a" : "1px solid #d1d5db",
                          background: selected ? "#caa96a" : "#ffffff",
                          color: selected ? "#111827" : disabled ? "#9ca3af" : "#1f2937",
                          fontWeight: selected ? 700 : 600,
                          fontSize: 13,
                          opacity: disabled ? 0.55 : 1,
                          whiteSpace: "nowrap",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            background: selected ? "#caa96a" : "#f8fafc",
                          },
                        }}
                      >
                        {slot}
                      </ButtonBase>
                    );
                  })}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  border: "1px solid #dbe3ef",
                  borderRadius: 2,
                  background: "#f8fafc",
                  p: 1.25,
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2" fontWeight={800} color="#1e293b">
                    Bis
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={endLabel || "--:--"}
                      sx={{
                        height: 22,
                        fontWeight: 700,
                        background: endLabel ? "#dbeafe" : "#e2e8f0",
                        color: endLabel ? "#1e40af" : "#64748b",
                      }}
                    />
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setEndTime(null)}
                      disabled={!endTime || isSaving}
                      sx={{
                        textTransform: "none",
                        minWidth: "auto",
                        px: 0.75,
                        color: "#64748b",
                        fontWeight: 700,
                      }}
                    >
                      Loschen
                    </Button>
                  </Stack>
                </Stack>
                <Typography variant="caption" color="#64748b" sx={{ display: "block", mb: 1 }}>
                  Verfügbare Zeit: {date ? (effectiveDay.isClosed ? "geschlossen" : effectiveRangeLabel) : `${operatingRange.openLabel} - ${operatingRange.closeLabel}`}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(4, minmax(0, 1fr))",
                      sm: "repeat(5, minmax(0, 1fr))",
                      md: "repeat(6, minmax(0, 1fr))",
                    },
                    gap: 1,
                  }}
                >
                  {timeSlots.map((slot) => {
                    const slotMinutes = toMinutes(slot);
                    const startMinutes = startLabel ? toMinutes(startLabel) : null;
                    // An end must sit inside the SAME effective interval as the start
                    // (a block may not span a closed gap).
                    const disabledByRange =
                      typeof slotMinutes !== "number" ||
                      (typeof startMinutes === "number"
                        ? !sameIntervalEnd(startMinutes, slotMinutes)
                        : !effectiveDay.intervals.some(
                            (iv) => slotMinutes > iv.startMin && slotMinutes <= iv.endMin
                          ));
                    const disabledByCurrentStartOverlap =
                      typeof startMinutes === "number" &&
                      doesRangeOverlapSelectedBlocks(startMinutes, slotMinutes);
                    const disabled =
                      disabledByRange ||
                      (typeof startMinutes === "number" && slotMinutes <= startMinutes) ||
                      disabledByCurrentStartOverlap;
                    const selected = endLabel === slot;

                    return (
                      <ButtonBase
                        key={`end-${slot}`}
                        onClick={() => setEndTime(makeTimeValue(slot))}
                        disabled={disabled || isSaving}
                        sx={{
                          width: "100%",
                          minWidth: 0,
                          height: 40,
                          borderRadius: 999,
                          px: 0.75,
                          border: selected ? "2px solid #caa96a" : "1px solid #d1d5db",
                          background: selected ? "#caa96a" : "#ffffff",
                          color: selected ? "#111827" : disabled ? "#9ca3af" : "#1f2937",
                          fontWeight: selected ? 700 : 600,
                          fontSize: 13,
                          opacity: disabled ? 0.55 : 1,
                          whiteSpace: "nowrap",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            background: selected ? "#caa96a" : "#f8fafc",
                          },
                        }}
                      >
                        {slot}
                      </ButtonBase>
                    );
                  })}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Hinweis (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="z.B. Luxuspaket wegen Wartung gesperrt"
                sx={{ background: "#f9fafb" }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleAdd}
                disabled={isSaving || !isFormReady}
                sx={{
                  height: "40px",
                  background: "#1d4ed8",
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: "0 6px 18px rgba(29,78,216,0.22)",
                  "&:hover": {
                    background: "#1e40af",
                    boxShadow: "0 8px 20px rgba(30,64,175,0.28)",
                  },
                }}
              >
                {isSaving ? "Speichern..." : "Blockierung speichern"}
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={clearForm}
                disabled={isSaving}
                sx={{
                  height: "40px",
                  fontWeight: 600,
                  textTransform: "none",
                  borderColor: "#cbd5e1",
                  color: "#334155",
                  background: "#ffffff",
                }}
              >
                Formular leeren
              </Button>
            </Grid>
        </Grid>
      </Box>

      {hasRequiredFields && !hasSelectedRangeConflict && (
        <Alert
          severity="info"
          sx={{
            mb: 2.5,
            border: "1px solid #bfdbfe",
            background: "#eff6ff",
          }}
        >
          {selectedPackages.length} Paket(e) werden am {format(date, "dd.MM.yyyy")} von{" "}
          {format(startTime, "HH:mm")} bis {format(endTime, "HH:mm")} blockiert.
        </Alert>
      )}
      {hasRequiredFields && hasSelectedRangeConflict && (
        <Alert
          severity="warning"
          sx={{
            mb: 2.5,
            border: "1px solid #fde68a",
            background: "#fffbeb",
          }}
        >
          Dieses Zeitfenster ist fur mindestens ein ausgewahltes Paket bereits blockiert.
          Bitte anderes Zeitfenster wahlen.
        </Alert>
      )}

      <Stack direction="row" spacing={1} alignItems="center" mb={1.25}>
        <PlaylistAddCheckCircleOutlinedIcon sx={{ fontSize: 18, color: "#334155" }} />
        <Typography variant="subtitle1" color="#334155" fontWeight={800}>
          Aktive Paket-Zeitblockierungen
        </Typography>
        {upcomingGroups.length > 0 && (
          <Chip
            size="small"
            label={`${upcomingGroups.length} Tag(e)`}
            sx={{
              height: 22,
              fontWeight: 700,
              background: "#dbeafe",
              color: "#1e40af",
              border: "1px solid #bfdbfe",
            }}
          />
        )}
      </Stack>

      {upcomingGroups.length === 0 ? (
        <Box
          sx={{
            border: "1px dashed #d1d5db",
            background: "#ffffff",
            borderRadius: 2,
            p: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <DoNotDisturbOnOutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
          <Typography variant="body2" color="#64748b">
            Keine aktiven (heutigen oder zukunftigen) Paket-Zeitblockierungen vorhanden.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={groupsGridSx}>
            {pagedUpcomingGroups.map((group) => renderGroupCard(group))}
          </Box>

          {upcomingTotalPages > 1 && (
            <Stack direction="row" justifyContent="center" mt={2}>
              <Pagination
                count={upcomingTotalPages}
                page={upcomingPage}
                onChange={(_, value) => setUpcomingPage(value)}
                color="primary"
                shape="rounded"
              />
            </Stack>
          )}
        </>
      )}

      {pastGroups.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <ButtonBase
            onClick={() => setShowPast((prev) => !prev)}
            sx={{
              width: "100%",
              justifyContent: "space-between",
              borderRadius: 2,
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
              px: 2,
              py: 1.25,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <HistoryRoundedIcon sx={{ fontSize: 18, color: "#64748b" }} />
              <Typography variant="subtitle2" color="#475569" fontWeight={800}>
                Vergangene Blockierungen
              </Typography>
              <Chip
                size="small"
                label={pastGroups.length}
                sx={{
                  height: 22,
                  fontWeight: 700,
                  background: "#e2e8f0",
                  color: "#475569",
                }}
              />
            </Stack>
            <ExpandMoreRoundedIcon
              sx={{
                color: "#64748b",
                transition: "transform 0.2s ease",
                transform: showPast ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </ButtonBase>

          <Collapse in={showPast} unmountOnExit>
            <Box sx={{ ...groupsGridSx, mt: 2 }}>
              {pagedPastGroups.map((group) => renderGroupCard(group, { muted: true }))}
            </Box>

            {pastTotalPages > 1 && (
              <Stack direction="row" justifyContent="center" mt={2}>
                <Pagination
                  count={pastTotalPages}
                  page={pastPage}
                  onChange={(_, value) => setPastPage(value)}
                  shape="rounded"
                />
              </Stack>
            )}
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default PackageTimeBlocks;
